
import React, { useEffect, useState } from 'react';
import { useAuth } from '../../../../context/AuthContext';
import { API_ENDPOINTS } from '../../../../utils/config';
import './RequestSummaryModal.scss';
import QuoteAttachmentsModal from '../../../admin-v2/dashboard/RFQQuotesReviewDashboard/QuoteAttachmentsModal';

interface RequestSummaryModalProps {
    isOpen: boolean;
    onClose: () => void;
    requestId: string;
    rfqName?: string;
}

interface QuoteSelection {
    supplier_id: string;
    supplier_name: string;
    total_price: number;
    total_price_display?: string;
    currency: string;
    lead_time: string;
    payment_terms: string;
    quote_valid_until?: string;
}

const RequestSummaryModal: React.FC<RequestSummaryModalProps> = ({
    isOpen,
    onClose,
    requestId,
    rfqName
}) => {
    const { token } = useAuth();
    const [loading, setLoading] = useState(false);
    const [selections, setSelections] = useState<QuoteSelection[]>([]);
    const [error, setError] = useState<string | null>(null);

    // Attachments State
    const [showAttachmentsModal, setShowAttachmentsModal] = useState(false);
    const [attachmentSupplier, setAttachmentSupplier] = useState<{ id: string; name: string } | null>(null);
    const [attachmentCounts, setAttachmentCounts] = useState<Record<string, number>>({});

    // Fetch attachment counts
    useEffect(() => {
        const fetchAttachmentCounts = async () => {
            if (!requestId || !token || selections.length === 0) return;

            const counts: Record<string, number> = {};
            const uniqueSupplierIds = Array.from(new Set(selections.map(s => s.supplier_id)));

            await Promise.all(uniqueSupplierIds.map(async (supplierId) => {
                try {
                    const response = await fetch(API_ENDPOINTS.REQUEST_COMMUNICATION_MESSAGES(requestId, String(supplierId)), {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    const result = await response.json();
                    if (result.success && result.data?.messages) {
                        let count = 0;
                        result.data.messages.forEach((msg: any) => {
                            if (msg.direction === 'received' && msg.attachments) {
                                count += msg.attachments.length;
                            }
                        });
                        counts[supplierId] = count;
                    }
                } catch (err) {
                    console.error(`Failed to fetch attachments for ${supplierId}`, err);
                }
            }));

            setAttachmentCounts(prev => ({ ...prev, ...counts }));
        };

        fetchAttachmentCounts();
    }, [requestId, token, selections]);

    // Fetch request details to get selections
    useEffect(() => {
        if (isOpen && requestId && token) {
            fetchRequestDetails();
        }
    }, [isOpen, requestId, token]);

    const fetchRequestDetails = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(API_ENDPOINTS.REQUEST_V2_DETAIL(requestId), {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) throw new Error('Failed to fetch request details');

            const data = await response.json();
            if (data.success && data.data) {
                // Get quotes_selections from data
                if (data.data.quotes_selections && Array.isArray(data.data.quotes_selections)) {
                    setSelections(data.data.quotes_selections);
                } else {
                    setSelections([]);
                }
            }
        } catch (err: any) {
            console.error('Error fetching summary:', err);
            setError('Could not load summary.');
        } finally {
            setLoading(false);
        }
    };

    const formatPrice = (quote: QuoteSelection) => {
        // If display string exists and is not empty, use it
        if (quote.total_price_display && quote.total_price_display !== '—') {
            return quote.total_price_display;
        }
        // Fallback to number formatting
        if (quote.total_price !== undefined && quote.total_price !== null) {
            const price = typeof quote.total_price === 'string' ? parseFloat(quote.total_price as any) : quote.total_price;
            if (!isNaN(price)) {
                return `${price.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${quote.currency || ''}`;
            }
        }
        return '—';
    };

    if (!isOpen) return null;

    return (
        <div className="request-summary-modal-overlay">
            <div className="request-summary-modal">
                <div className="request-summary-modal__header">
                    <h3>Formatted Summary for {rfqName || 'RFQ'}</h3>
                    <button className="close-btn" onClick={onClose}>&times;</button>
                </div>

                <div className="request-summary-modal__content">
                    {loading ? (
                        <div className="loading-state">Loading summary...</div>
                    ) : error ? (
                        <div className="error-state">{error}</div>
                    ) : selections.length === 0 ? (
                        <div className="empty-state">No selections found for this request.</div>
                    ) : (
                        <div className="quotes-list">
                            <div className="quotes-header">
                                <div className="col-supplier">Selected Supplier</div>
                                <div className="col-price">Total Price</div>
                                <div className="col-delivery">Lead Time</div>
                                <div className="col-payment">Payment</div>
                            </div>
                            {selections.map((quote, index) => (
                                <div key={quote.supplier_id || index} className="quote-row">
                                    <div className="col-supplier">
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                            <span className="supplier-name">{quote.supplier_name}</span>
                                            <button
                                                onClick={() => {
                                                    setAttachmentSupplier({
                                                        id: quote.supplier_id,
                                                        name: quote.supplier_name
                                                    });
                                                    setShowAttachmentsModal(true);
                                                }}
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    padding: 0,
                                                    color: '#2563eb',
                                                    textDecoration: 'underline',
                                                    cursor: 'pointer',
                                                    fontSize: '12px',
                                                    textAlign: 'left'
                                                }}
                                            >
                                                {`View Attachments ${typeof attachmentCounts[quote.supplier_id] === 'number' ? `(${attachmentCounts[quote.supplier_id]})` : ''}`}
                                            </button>
                                        </div>
                                    </div>
                                    <div className="col-price">
                                        {formatPrice(quote)}
                                    </div>
                                    <div className="col-delivery">{quote.lead_time || '-'}</div>
                                    <div className="col-payment">{quote.payment_terms || '-'}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="request-summary-modal__footer">
                    <div className="actions">
                        <button className="btn-close" onClick={onClose}>Close</button>
                    </div>
                </div>
            </div>
            {attachmentSupplier && requestId && (
                <QuoteAttachmentsModal
                    isOpen={showAttachmentsModal}
                    onClose={() => {
                        setShowAttachmentsModal(false);
                        setAttachmentSupplier(null);
                    }}
                    requestId={requestId}
                    supplierId={attachmentSupplier.id}
                    supplierName={attachmentSupplier.name}
                />
            )}
        </div>
    );
};

export default RequestSummaryModal;
